import React, {
  ElementType,
  MouseEventHandler,
  ReactNode,
  useEffect,
  useRef,
  useState
} from 'react'

import { useIsMobile } from 'utils/hooks'
import { useTranslation } from 'language/translator'

import CustomIcon from 'components_new/atoms/CustomIcon'
import Box from 'components_new/atoms/Box'
import Dialog, { DialogProps } from 'components_new/atoms/Dialog'
import Divider from 'components_new/atoms/Divider'
import Drawer from 'components_new/atoms/Drawer'
import IconButton from 'components_new/atoms/IconButton'
import Skeleton from 'components_new/atoms/Skeleton'
import Tooltip from 'components_new/atoms/Tooltip'

import LoadingSection from '../LoadingSection'
import SimpleSwipeableDrawer from '../SimpleSwipeableDrawer'

import {
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  Slide
} from '@mui/material'

import { TransitionProps } from '@mui/material/transitions'

/** Px shifted right per stacked dialog layer — see `depth`/`dimmed`. */
export const STACK_OFFSET = 64

export interface SidebarDialogProps {
  actions?: ReactNode
  bindScrollToTop?: (fn: () => void) => void
  /*--
  Usage of bindScrollToTop in parent component:
    const [scrollToTop, setScrollToTop] = useState<() => void>(() => () => {})
    const bindScrollHandler = useCallback((fn: () => void) => {
      setScrollToTop(() => fn)
    }, [])
  --*/
  children: ReactNode
  component?: ElementType<any>
  contentText?: ReactNode
  depth?: number
  disableBackdrop?: boolean
  disableOverflow?: boolean
  footer?: ReactNode
  fullHeight?: boolean
  fullWidth?: boolean
  hero?: ReactNode
  hideBackdrop?: DialogProps['hideBackdrop']
  leftSidebar?: {
    collapsed?: boolean
    content?: ReactNode
    disablePadding?: boolean
    setCollapsed?: React.Dispatch<React.SetStateAction<boolean>>
    size?: TSideBarSize
  }
  loading?: boolean
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false
  noGutter?: boolean
  onClose: () => void
  onMouseDown?: MouseEventHandler<HTMLElement>
  open: boolean
  PaperProps?: object
  rightSidebar?: {
    collapsed?: boolean
    content?: ReactNode
    disablePadding?: boolean
    setCollapsed?: React.Dispatch<React.SetStateAction<boolean>>
    // width?: number
    size?: TSideBarSize
  }
  scrollToTop?: boolean
  sx?: object
  title?: ReactNode
  titleActions?: ReactNode
  toolbar?: ReactNode
}

type TSideBarSize = 'small' | 'medium' | 'large' | 'xlarge'

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>
  },
  ref: React.Ref<unknown>
) {
  return (
    <Slide
      direction="left"
      easing={{ enter: 'ease-out', exit: 'ease-in' }}
      ref={ref}
      timeout={400}
      {...props}
    />
  )
})

/**
 * The SidebarDialog component is a
 * variant of a Dialog that is the size
 * of an Advanced, but comes with sidebar
 * features.
 */

const SidebarDialog = React.forwardRef(
  (props: SidebarDialogProps, ref: any) => {
    const {
      actions,
      bindScrollToTop,
      children,
      depth,
      disableOverflow,
      footer,
      fullHeight,
      fullWidth = true,
      hero,
      hideBackdrop,
      leftSidebar,
      loading,
      maxWidth = 'md',
      noGutter,
      onClose,
      open,
      PaperProps,
      rightSidebar,
      title,
      titleActions,
      toolbar,
      ...rest
    } = props

    const isMobile = useIsMobile()
    const theme = useTheme()
    const contentRef = useRef<HTMLElement>(null)
    const { t } = useTranslation()

    const isControlled =
      rightSidebar?.collapsed !== undefined &&
      rightSidebar?.setCollapsed !== undefined

    const [internalRightSidebarOpen, setInternalRightSidebarOpen] =
      useState<boolean>(
        rightSidebar ? !rightSidebar.collapsed && !isMobile : false
      )

    const rightSidebarOpen = isControlled
      ? !rightSidebar.collapsed
      : internalRightSidebarOpen

    const handleToggleRightSidebar = (value?: boolean) => {
      if (isControlled) {
        const next = value !== undefined ? !value : !rightSidebar.collapsed

        if (rightSidebar?.setCollapsed) {
          rightSidebar.setCollapsed(next)
        }
      } else {
        setInternalRightSidebarOpen(
          value !== undefined ? value : !internalRightSidebarOpen
        )
      }
    }

    const isLeftControlled =
      leftSidebar?.collapsed !== undefined &&
      leftSidebar?.setCollapsed !== undefined

    const [internalLeftSidebarOpen, setInternalLeftSidebarOpen] =
      useState<boolean>(
        leftSidebar ? !leftSidebar.collapsed && !isMobile : false
      )

    const leftSidebarOpen = isLeftControlled
      ? !leftSidebar.collapsed
      : internalLeftSidebarOpen

    const handleToggleLeftSidebar = (value?: boolean) => {
      if (isLeftControlled) {
        const next = value !== undefined ? !value : !leftSidebar.collapsed

        if (leftSidebar?.setCollapsed) {
          leftSidebar.setCollapsed(next)
        }
      } else {
        setInternalLeftSidebarOpen(
          value !== undefined ? value : !internalLeftSidebarOpen
        )
      }
    }

    const getSidebarWidth = (size: TSideBarSize | undefined) => {
      switch (size) {
      case 'small': {
        return 256
      }
      case 'medium': {
        return 384
      }
      case 'large': {
        return 512
      }
      case 'xlarge': {
        return 720
      }
      default: {
        return 384 // medium
      }
      }
    }

    useEffect(() => {
      if (bindScrollToTop) {
        bindScrollToTop(() => {
          if (contentRef.current) {
            contentRef.current.scrollTo({ top: 0, behavior: 'auto' })
          }
        })
      }
    }, [bindScrollToTop])

    // Reset both sidebars to open every time the dialog opens, so a collapsed
    // state from a previous session does not carry over to the next.
    useEffect(() => {
      if (open) {
        setInternalRightSidebarOpen(true)
        setInternalLeftSidebarOpen(true)
      }
    }, [open])

    if (isMobile)
      return (
        <SimpleSwipeableDrawer
          onClose={onClose}
          onOpen={() => {}}
          open={open}
          title={title}
        >
          {children}
        </SimpleSwipeableDrawer>
      )

    return (
      <Dialog
        fullWidth={fullWidth}
        hideBackdrop={hideBackdrop}
        maxWidth={maxWidth}
        onClose={onClose}
        onMouseDown={(e) => e.stopPropagation()}
        open={open}
        PaperProps={{
          sx: {
            borderBottomLeftRadius: depth ? 0 : undefined,
            position: 'relative',
            ...(depth ? { marginLeft: `${32 + depth * STACK_OFFSET}px` } : null)
          },
          ...PaperProps
        }}
        slotProps={{
          backdrop: {
            sx: {
              backdropFilter: 'grayscale(100%)',
              ...(depth
                ? {
                    borderRadius: 1,
                    margin: '32px',
                    marginLeft: `${32 + (depth - 1) * STACK_OFFSET}px`
                  }
                : null)
            }
          }
        }}
        {...rest}
        ref={ref}
        TransitionComponent={depth ? Transition : undefined}
      >
        {title ? (
          <DialogTitle
            component={'div'}
            sx={{
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'space-between'
            }}
          >
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                gap: 1
              }}
            >
              {leftSidebar ? (
                <>
                  <Tooltip
                    title={
                      leftSidebarOpen
                        ? t('global', 'close_side_panel')
                        : t('global', 'open_side_panel')
                    }
                  >
                    <IconButton
                      onClick={() => handleToggleLeftSidebar()}
                      size="small"
                    >
                      <CustomIcon
                        name="LeftSidebar"
                        style={{ height: 20, width: 20 }}
                      />
                    </IconButton>
                  </Tooltip>

                  <Divider flexItem={true} orientation="vertical" />
                </>
              ) : null}
              {title}
            </Box>
            {titleActions || rightSidebar ? (
              <Box
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  gap: 1
                }}
              >
                {titleActions}

                {rightSidebar ? (
                  <>
                    <Divider flexItem={true} orientation="vertical" />

                    <Tooltip
                      title={
                        rightSidebarOpen
                          ? t('global', 'close_side_panel')
                          : t('global', 'open_side_panel')
                      }
                    >
                      <IconButton
                        onClick={() => handleToggleRightSidebar()}
                        size="small"
                      >
                        <CustomIcon
                          name="RightSidebar"
                          style={{ height: 20, width: 20 }}
                        />
                      </IconButton>
                    </Tooltip>
                  </>
                ) : null}
              </Box>
            ) : null}
          </DialogTitle>
        ) : null}

        <DialogContent
          dividers={true}
          sx={{
            p: 0,
            overflow: 'hidden',
            height: fullHeight ? '90vh' : undefined,
            position: 'relative',
            display: 'flex',
            flexDirection: 'row',
            transition: 'all 0.5s ease'
          }}
        >
          {leftSidebar && !isMobile ? (
            <Drawer
              anchor="left"
              open={leftSidebarOpen}
              PaperProps={{
                sx: {
                  bgcolor: 'background.default',
                  boxSizing: 'border-box',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  p: leftSidebar.disablePadding ? 0 : 2,
                  position: 'absolute',
                  transition: 'all 0.1s',
                  width: getSidebarWidth(leftSidebar?.size),
                  zIndex: 0
                }
              }}
              sx={{
                flexShrink: 0,
                transition: 'all 0.1s',
                width: getSidebarWidth(leftSidebar?.size)
              }}
              variant="persistent"
            >
              {loading
                ? [1, 2, 3].map((i) => (
                    <Skeleton
                      key={i}
                      width={i === 3 ? '50%' : undefined}
                      sx={{ mb: 1 }}
                    />
                  ))
                : leftSidebar.content}
            </Drawer>
          ) : null}

          <Box
            sx={{
              display: 'flex',
              flex: 1,
              flexDirection: 'column',
              minWidth: 0,
              position: 'relative',
              ml:
                !leftSidebar || leftSidebarOpen || isMobile
                  ? 0
                  : `-${getSidebarWidth(leftSidebar?.size)}px`,
              mr:
                !rightSidebar || rightSidebarOpen || isMobile
                  ? 0
                  : `-${getSidebarWidth(rightSidebar?.size)}px`,
              transition: theme.transitions.create('margin', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen
              })
            }}
          >
            {toolbar ? (
              <Box
                sx={{
                  alignItems: 'center',
                  bgcolor: 'background.default',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  gap: 0.5,
                  height: 40,
                  position: 'sticky',
                  px: 2,
                  top: 0,
                  zIndex: 2
                }}
              >
                {toolbar}
              </Box>
            ) : null}

            <Box
              className="DialogContent"
              ref={contentRef}
              sx={{
                display: 'flex',
                flex: 1,
                flexDirection: 'column',
                minHeight: 96,
                overflow: disableOverflow ? 'hidden' : 'auto',
                py: hero || noGutter ? 0 : 2,
                position: 'relative'
              }}
            >
              {hero ? (
                <Box
                  sx={{
                    bgcolor: 'background.paper',
                    position: 'sticky',
                    px: noGutter ? 0 : 3,
                    py: 2,
                    top: 0,
                    zIndex: 2
                  }}
                >
                  <Box sx={{ position: 'relative', width: '100%' }}>{hero}</Box>
                </Box>
              ) : null}
              <Box
                sx={{
                  display: 'flex',
                  flex: 1,
                  flexDirection: 'column',
                  // Lets children that scroll themselves fit the dialog.
                  minHeight: disableOverflow ? 0 : undefined,
                  position: 'relative',
                  px: noGutter ? 0 : 3
                }}
              >
                {loading ? (
                  <>
                    <Skeleton width={'50%'} sx={{ m: 2 }} />
                    <LoadingSection
                      disableLoadingDescription={true}
                      sx={{ minHeight: 400 }}
                    />
                  </>
                ) : (
                  children
                )}
              </Box>
            </Box>
          </Box>

          {rightSidebar && !isMobile ? (
            <Drawer
              anchor="right"
              open={rightSidebarOpen}
              PaperProps={{
                sx: {
                  bgcolor: 'background.default',
                  boxSizing: 'border-box',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  p: rightSidebar.disablePadding ? 0 : 2,
                  position: 'absolute',
                  transition: 'all 0.1s',
                  width: getSidebarWidth(rightSidebar?.size),
                  zIndex: 0
                }
              }}
              sx={{
                flexShrink: 0,
                transition: 'all 0.1s',
                width: getSidebarWidth(rightSidebar?.size)
              }}
              variant="persistent"
            >
              {rightSidebar.content}
            </Drawer>
          ) : null}
        </DialogContent>
        {footer ? (
          <Box
            sx={{
              flex: '0 0 auto',
              borderBottom: '1px solid',
              borderColor: 'divider'
            }}
          >
            {footer}
          </Box>
        ) : null}
        {actions ? (
          <DialogActions>
            {loading
              ? [1, 2].map((i) => <Skeleton key={i} height={32} width={64} />)
              : actions}
          </DialogActions>
        ) : null}
      </Dialog>
    )
  }
)

SidebarDialog.displayName = 'SidebarDialog'
export default SidebarDialog
